'use client';

import { useCallback } from 'react';
import {
	BN,
	SwapMode,
	SpotBalanceType,
	ZERO,
	SwapQuote,
} from '@velocity-exchange/sdk';
import {
	UIMarket,
	MarketId,
	matchEnum,
	JLP_POOL_ID,
} from '@velocity-exchange/common';
import { Adapter } from '@solana/wallet-adapter-base';

import useVelocityClient from 'src/hooks/useVelocityClient';
import useGetOraclePriceForMarket from 'src/hooks/useGetOraclePriceForMarket';
import useVelocityStore from 'src/stores/VelocityStore/useVelocityStore';
import useVelocityAccountsStore from 'src/stores/useVelocityAccountsStore';
import useTxDataStore from 'src/stores/useTxDataStore';
import { useGetTxParams } from 'src/hooks/velocityActions/transactions/useGetTxParams';
import { notify, NotificationProps } from 'src/utils/notifications';
import TransactionErrorHandler, {
	AnchorError,
	isTransactionTooLargeError,
} from 'src/utils/TransactionErrorHandler/TransactionErrorHandler';
import { buildSwapTxWithRetryLogic } from './helpers/buildSwapTxWithRetryLogic';
import { JLP_POOL_JLP_MARKET_INDEX } from 'src/environmentVariables/EnvironmentVariables';
import { isAmplifyAccountName } from 'src/utils/amplify';
import { AccountData } from 'src/stores/useVelocityAccountsStore';
import { useSponsoredSignVelocityActionWithFallback } from 'src/hooks/wallets/useSponsoredSignVelocityActionWithFallback';
import { runOrderActionToast } from 'src/hooks/velocityActions/transactions/runOrderActionToast';
import { UiEnhancedIWalletV2 } from 'src/utils/wallets/IWalletV2';
import invariant from 'tiny-invariant';
import { MAX_COMPUTE_UNITS } from 'src/constants/constants';

interface SwapProps {
	userKey: string;
	amount: BN;
	fromMarketIndex: number;
	toMarketIndex: number;
	swapMode: SwapMode;
	quote: SwapQuote;
	onlyDirectRoutes?: boolean;
	slippageBps?: number;
	/** Route account budget the quote was fetched with. */
	maxAccounts?: number;
}

/**
 * Hook for executing spot swaps with retry logic.
 */
export const useSwap = () => {
	const velocityClient = useVelocityClient();
	const swapClient = useVelocityStore((s) => s.swapClient);
	const wallet = useVelocityStore((s) => s.wallet?.current?.adapter);
	const getAccountsState = useVelocityAccountsStore((s) => s.get);
	const getOraclePriceForMarket = useGetOraclePriceForMarket();
	const addTxErrorToTxDataStore = useTxDataStore((s) => s.addNewTxError);
	const getTxParams = useGetTxParams();

	const swap = useSponsoredSignVelocityActionWithFallback(
		useCallback(
			async (
				signerWallet: UiEnhancedIWalletV2,
				toastId: string,
				swapProps: SwapProps
			): Promise<boolean> => {
				const {
					userKey,
					amount,
					fromMarketIndex,
					toMarketIndex,
					swapMode,
					quote,
					onlyDirectRoutes = false,
					slippageBps = 100,
					maxAccounts,
				} = swapProps;

				if (!quote) {
					notify({
						type: 'error',
						message: 'Swap Failed',
						description: 'Could not fetch swap quote. Please try again',
						id: toastId,
					});

					return false;
				}

				invariant(velocityClient, 'driftClient is required');
				invariant(swapClient, 'swapClient is required');

				const accountsState = getAccountsState();
				const accountData = accountsState.accounts[userKey] as AccountData;

				// block the swap if it's opening a borrow on the deposit asset in an amplify account
				if (isAmplifyAccountName(accountData.name)) {
					// add to this when more pools get added
					if (
						accountData.poolId === JLP_POOL_ID &&
						fromMarketIndex === JLP_POOL_JLP_MARKET_INDEX
					) {
						const userDepositAssetBalance = accountData.spotBalances.find(
							(bal) =>
								bal.asset.marketIndex === JLP_POOL_JLP_MARKET_INDEX &&
								matchEnum(bal.balanceType, SpotBalanceType.DEPOSIT)
						)?.balance;

						if (amount.gt(userDepositAssetBalance?.val ?? ZERO)) {
							notify({
								type: 'error',
								message: 'Invalid Amplify Position',
								id: toastId,
							});
							return false;
						}
					}
				}

				velocityClient?.switchActiveUser(
					accountData.userId,
					accountData.authority
				);

				const inMarketConfig = UIMarket.createSpotMarket(fromMarketIndex);
				const outMarketConfig = UIMarket.createSpotMarket(toMarketIndex);

				const toastProps: NotificationProps = {
					id: toastId,
					type: 'awaiting_wallet_confirmation',
					showUntilCancelled: true,
					timeoutIfProlongedTxn: true,
					updatePrevious: true,
					overrideBlockSubsequentToasts: true,
					blockSubsequentToasts: false,
					action: undefined,
				};

				const swapResult = await runOrderActionToast({
					toastId,
					pendingToastProps: toastProps,
					run: async () => {
						const oraclePrice = getOraclePriceForMarket(
							MarketId.createSpotMarket(toMarketIndex)
						);

						const usdEstimate = Number(quote.outAmount) * oraclePrice.toNum();

						const accountPubKey = accountData.pubKey;

						const slippage = slippageBps / 100;

						const { ixs, lookupTables, success, error } =
							await buildSwapTxWithRetryLogic({
								accountPubKey,
								inputMarketConfig: inMarketConfig.market as any,
								outputMarketConfig: outMarketConfig.market as any,
								amount,
								swapMode,
								slippage,
								usdEstimate,
								maxRetries: 5,
								additionalIxs: [],
								onlyDirectRoutes,
								quoteToUse: quote,
								maxAccounts,
								velocityClient,
								swapClient,
								getTxParams,
								useFeePayer: signerWallet.usesFeePayer,
							});

						if (!success) {
							// Rethrow the last attempt's error so the handler can classify it
							// (oversized tx, rate limiting, on-chain failure) instead of
							// showing generic copy for every cause.
							throw error ?? new Error('Swap simulation failed');
						}

						const txParams = getTxParams({
							computeUnits: MAX_COMPUTE_UNITS,
							useSimulatedComputeUnits: false,
						});

						await signerWallet.signAndSendTransaction(
							ixs,
							txParams,
							{
								onSendTransactionStart: () => {
									notify({
										...toastProps,
										type: 'info',
										message: 'Sending swap.',
									});
								},
							},
							lookupTables
						);

						return true;
					},
					success: { message: 'Swap successful.' },
					handleError: (err, id) => {
						addTxErrorToTxDataStore({
							error:
								(err as { message?: string })?.message ?? 'Unknown swap error',
							type: 'swap',
							ts: Date.now(),
						});

						const customErrorHandler = (anchorErr: AnchorError) => {
							// # Transaction size too large - override with swap-specific
							// guidance. Detection lives in TransactionErrorHandler; this only
							// overrides the copy for this flow.
							if (isTransactionTooLargeError(anchorErr)) {
								notify({
									type: 'error',
									message: 'Transaction error.',
									description: `Some swaps on Velocity will fail if the swap route is too complex. Please try again using the "Use direct route" option.`,
									id,
									updatePrevious: true,
									subDescription: undefined,
								});

								return true;
							}

							return false;
						};

						TransactionErrorHandler.handleError({
							error: err as AnchorError,
							toastId: id,
							customErrorHandler,
							wallet: wallet as Adapter,
						});
					},
				});

				return swapResult ?? false;
			},
			[
				velocityClient,
				swapClient,
				getAccountsState,
				getOraclePriceForMarket,
				addTxErrorToTxDataStore,
				getTxParams,
				wallet,
			]
		)
	);

	return { swap };
};
